import { motion } from "framer-motion";
import { Award, Clock, Headphones, ShieldCheck, Users, Wrench } from "lucide-react";
import { FaBuilding, FaCity, FaHospital, FaIndustry, FaRoad, FaSchool } from "react-icons/fa";
import { HiArrowRight } from "react-icons/hi";
import { MdCheckCircle } from "react-icons/md";
import { Link } from 'react-router-dom';
import firebg from "../assets/firebg.jpg";
import fireimg from "../assets/fireimg2.jpg";
import abt from '../assets/frabt.jpg';

const industries = [
  { icon: <FaBuilding className="text-yellow-500 text-6xl mb-4" />, title: "Residential Fire Safety", description: "Installation and maintenance of fire alarms, extinguishers, and sprinkler systems for homes and apartments." },
  { icon: <FaCity className="text-yellow-500 text-6xl mb-4" />, title: "Commercial Fire Protection", description: "Comprehensive fire safety solutions for offices, malls, and retail spaces." },
  { icon: <FaIndustry className="text-yellow-500 text-6xl mb-4" />, title: "Industrial Fire Systems", description: "Custom fire suppression and detection systems for factories, warehouses, and plants." },
  { icon: <FaSchool className="text-yellow-500 text-6xl mb-4" />, title: "Educational Institutions", description: "Ensuring safety in schools and universities through fire equipment installation and upkeep." },
  { icon: <FaHospital className="text-yellow-500 text-6xl mb-4" />, title: "Healthcare Facilities", description: "Protecting hospitals and clinics with reliable fire prevention and emergency systems." },
  { icon: <FaRoad className="text-yellow-500 text-6xl mb-4" />, title: "Infrastructure & Public Spaces", description: "Installing and maintaining fire safety systems for roads, bridges, and public buildings." },
];


const reasons = [
  { icon: <ShieldCheck size={32} />, title: "Certified Safety", text: "We follow industry standards and ensure every installation meets fire safety regulations." },
  { icon: <Wrench size={32} />, title: "Expert Maintenance", text: "Regular inspections and reliable maintenance keep your systems always ready." },
  { icon: <Clock size={32} />, title: "24/7 Support", text: "Our team is available round-the-clock for urgent fire safety needs." },
  { icon: <Users size={32} />, title: "Experienced Team", text: "Trained professionals with years of expertise in fire equipment solutions." },
  { icon: <Award size={32} />, title: "Proven Reliability", text: "Trusted by businesses and organizations for consistent and dependable service." },
  { icon: <Headphones size={32} />, title: "Customer Focus", text: "We listen, advise, and deliver fire safety tailored to your needs." },
];

function FirePage() {

  return (
    <div>
      <section
        className="relative w-full h-[30vh] md:h-[40vh] bg-cover bg-center"
        style={{ backgroundImage: `url(${firebg})` }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-75"></div>
        <div className="relative z-10 flex flex-col items-start justify-center h-full px-6 md:px-12 max-w-6xl mx-auto text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Fire system</h1>
          <div className="flex items-center text-sm md:text-base space-x-2 text-gray-200">
            <Link to="/" className="hover:text-white cursor-pointer">Home</Link>
            <span>/</span>
            <span className="text-gray-300 underline hover:text-white cursor-pointer"> Fire service</span>
          </div>
        </div>
      </section>

      <section className="w-full py-16 bg-white">
        <div className="mx-auto max-w-6xl flex flex-col md:flex-row items-stretch gap-10 px-6">

          {/* Image */}
          <motion.img
            src={abt}
            alt="Fire Equipment Installation & Maintenance"
            loading="lazy"
            className="w-full md:w-1/2 h-96 md:h-auto object-cover rounded-lg shadow-lg"
            initial={{ x: -100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          />

          {/* Content */}
          <motion.div
            className="flex flex-col gap-6 md:w-1/2 h-full"
            initial={{ x: 100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
              Reliable Fire Safety Solutions
            </h2>
            <p className="text-gray-600 text-base md:text-lg">
              We specialize in the installation, maintenance, and inspection of fire safety equipment.
              From fire alarms to suppression systems, our expert team ensures your property is protected and compliant with safety standards.
            </p>

            {/* Tick List */}
            <div className="flex flex-col gap-3 mt-4">
              {[
                "Istallation of fire alarms and detectors.",
                "Setup of fire extinguishers and suppression systems",
                "Placement of illuminated fire exit signs and emergency lighting.",
                "Installation of sprinkler systems and emergency evacuation equipment"
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                >
                  <MdCheckCircle className="text-gray-900 w-6 h-6 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{item}</p>
                </motion.div>
              ))}
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-6">
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center justify-center gap-3 px-6 py-3 bg-primary text-black font-medium rounded-md transition-all w-full sm:w-auto"
              >
                Contact Us
                <span className="bg-white p-2 rounded-full flex items-center justify-center">
                  <HiArrowRight className="w-5 h-5 text-black" />
                </span>
              </motion.a>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/projects"
                  className="inline-flex items-center justify-center gap-3 px-6 py-3 bg-secondary text-primary font-medium rounded-md transition-all w-full sm:w-auto"
                >
                  Our Projects
                  <span className="bg-white p-2 rounded-full flex items-center justify-center">
                    <HiArrowRight className="w-5 h-5 text-black" />
                  </span>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-12 text-center bg-gray-50">
        <h2 className="text-3xl font-bold text-gray-800 mb-12">Our Fire Services</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {industries.map((industry, index) => (
            <div
              key={index}
              className="bg-white p-8 rounded-lg shadow-lg transform hover:-translate-y-3 hover:scale-105 transition duration-500 cursor-pointer group flex flex-col items-center text-center"
            >
              <div className="mb-4">{industry.icon}</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">{industry.title}</h3>
              <p className="text-gray-600 group-hover:text-yellow-500 transition">{industry.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full bg-secondary py-16 mb-16">
        <div className="flex flex-col lg:flex-row mx-auto max-w-6xl gap-12 px-2 items-start">

          {/* Image only visible on large screens and up */}
          <motion.img
            src={fireimg}
            alt="Fire Safety"
            loading="lazy"
            className="hidden lg:block w-full lg:w-1/2 rounded-lg shadow-lg object-cover max-h-[450px]"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          />

          <motion.div
            className="flex flex-col w-full lg:w-1/2 justify-center gap-6"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div>
              <p className="uppercase text-primary font-semibold tracking-wide mb-2">
                Why Choose Us
              </p>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-8 leading-snug">
                <span className="text-gray-300">Your Safety, Our Priority</span>
              </h1>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 place-items-center">
              {reasons.map((reason, index) => (
                <motion.div
                  key={index}
                  className="group bg-secondary/90 border border-gray-700 rounded-lg p-4 w-48 hover:bg-primary hover:shadow-lg transition flex flex-col items-center justify-center text-center gap-3"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="text-primary text-3xl group-hover:text-black">
                    {reason.icon}
                  </div>
                  <h3 className="text-base font-semibold text-white group-hover:text-black">
                    {reason.title}
                  </h3>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="w-full mb-16">
        <div className="flex flex-col justify-center items-center mx-auto max-w-6xl py-12 bg-gray-50 rounded-lg gap-8">

          {/* Centered Text */}
          <motion.div
            className="flex flex-col items-center w-3/4 space-y-6 text-center"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-black leading-snug">
              Ready For Fire System Service?
            </h1>
            <p className="text-gray-900 text-base md:text-lg leading-relaxed">
              Ensure your safety with our reliable fire detection and suppression solutions.
              Our team is ready to design, install, and maintain a system tailored to your needs.
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary text-black px-6 py-3 rounded-lg font-semibold shadow-md hover:bg-primary/80 transition"
              >
                Contact Us
              </motion.button>
            </Link>
          </motion.div>

        </div>
      </section>

    </div>
  )
}

export default FirePage
